import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleIcon, ShieldCheckIcon, HomeIcon, UserIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface VoteSummary {
  electionId: string;
  electionTitle: string;
  electionPosition: string;
  candidateId: string | null;
  candidateName: string;
  isAbstention: boolean;
}

const VoteConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer les données depuis l'état de navigation
  const voteSummary = location.state?.voteSummary || [];
  const voteIds = location.state?.voteIds || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 flex items-center justify-center">
      <div className="max-w-2xl mx-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* En-tête de succès */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-12 text-center">
            <CheckCircleIcon className="w-24 h-24 text-white mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">
              Vote Enregistré avec Succès
            </h1>
            <p className="text-green-100 text-lg">
              Votre vote a été enregistré de manière sécurisée
            </p>
          </div>

          {/* Contenu */}
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Merci pour votre participation
              </h2>
              <p className="text-gray-600 mb-6">
                Votre vote a été enregistré de manière anonyme et sécurisée. 
                Les résultats seront publiés après la clôture de toutes les élections.
              </p>
            </div>

            {/* Informations de confirmation */}
            {voteIds.length > 0 && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Votes enregistrés avec succès
                    </p>
                    <p className="text-xs text-green-600">
                      {voteIds.length} vote(s) sauvegardé(s) en base de données
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Récapitulatif des votes enregistrés */}
            {voteSummary.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Récapitulatif de vos votes enregistrés
                </h3>
                <div className="space-y-3">
                  {voteSummary.map((vote: VoteSummary, index: number) => (
                    <div key={vote.electionId} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{vote.electionTitle}</p>
                            <p className="text-sm text-gray-600">{vote.electionPosition}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {vote.isAbstention ? (
                            <div className="flex items-center text-orange-600">
                              <XCircleIcon className="w-4 h-4 mr-1" />
                              <span className="text-sm font-medium">Abstention</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-green-600">
                              <UserIcon className="w-4 h-4 mr-1" />
                              <span className="text-sm font-medium">{vote.candidateName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Informations de sécurité */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-start">
                <ShieldCheckIcon className="w-6 h-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Sécurité de votre vote
                  </h3>
                  <ul className="text-blue-800 space-y-1">
                    <li>• Votre vote est anonyme et ne peut pas être tracé</li>
                    <li>• Les données sont chiffrées et sécurisées</li>
                    <li>• Aucune modification n'est possible après validation</li>
                    <li>• Les résultats seront publiés de manière transparente</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <HomeIcon className="w-5 h-5 mr-2" />
                Retour à l'accueil
              </button>
              
              <button
                onClick={() => window.close()}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
              >
                Fermer cette fenêtre
              </button>
            </div>
          </div>

          {/* Pied de page */}
          <div className="bg-gray-50 px-8 py-4 text-center">
            <p className="text-sm text-gray-500">
              Système de vote électronique ASPROCOM - Sécurisé et transparent
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoteConfirmationPage;
